import { TRIGGER_METADATA } from 'modules/workflows/constants';
import { createSelector } from 'reselect';
import { NodeId, SEND_ACTION_NODE_TYPES } from 'ui/workflows/flowchart';
import { WorkflowsRootState } from '../ducks.types';

export const getNodeMap = (state: WorkflowsRootState) => state.workflow?.nodes || {};

export const getNodes = (state: WorkflowsRootState) => Object.values(getNodeMap(state));
export const getErrorNodes = createSelector([getNodes], (nodes) =>
  nodes.filter((node) => !!node.hasError),
);
export const getSelectedAINodes = createSelector([getNodes], (nodes) =>
  nodes.filter((node) => !!node.isAIGenerated),
);
export const getParentIds = createSelector([getNodes], (nodes) => {
  return nodes.reduce((acc: string[], node) => {
    const { parentId } = node;
    return parentId && !acc.includes(parentId) ? [...acc, parentId] : acc;
  }, []);
});
export const getCombos = createSelector([getNodes], (nodes) => {
  const combos = nodes.reduce((acc: Record<string, string>, node) => {
    const { comboId } = node;
    return comboId && !acc[comboId] ? { ...acc, [comboId]: { id: comboId } } : acc;
  }, {});
  return Object.values(combos);
});
export const getSelectedNodes = createSelector([getNodes], (nodes) =>
  nodes.filter((node) => !!node.isSelected),
);
export const getNodeById = (state: WorkflowsRootState, nodeId?: NodeId | null) =>
  getNodeMap(state)[nodeId as string] || {};
export const getNextNodeId = (state: WorkflowsRootState) => state.workflow?.nextNodeId || -1;
export const getNodeIsOpening = (state: WorkflowsRootState) => state.workflow?.isNodeOpening;
export const getNodeResources = (state: WorkflowsRootState, nodeId?: NodeId | null) =>
  getNodeById(state, nodeId)?.resources;
export const getOriginalNodes = (state: WorkflowsRootState) => state.workflow?.originalNodes || [];
export const getOpenedNodeId = (state: WorkflowsRootState) => state.workflow?.openedNodeId;
export const getOpenedNodeData = (state: WorkflowsRootState) =>
  getNodeById(state, getOpenedNodeId(state))?.nodeData;

export const getIsNodeEditorAsyncLoading = (state: WorkflowsRootState) =>
  state.workflow?.isNodeEditorAsyncLoading;

/**
 * Start Node
 */
export const getStartNodeId = (state: WorkflowsRootState) =>
  state.workflow?.workflow?.startWorkflowNodeId;
export const getStartNode = (state: WorkflowsRootState) =>
  getNodeMap(state)[`${getStartNodeId(state)}`];
export const getStartNodeTriggerNodeClass = (state: WorkflowsRootState) =>
  getStartNode(state)?.nodeData?.nodeClass;
export const getStartNodeTriggerEventName = (state: WorkflowsRootState) =>
  getStartNode(state)?.nodeData?.eventName;
export const getStartNodeTriggerDataType = (state: WorkflowsRootState) =>
  TRIGGER_METADATA[getStartNodeTriggerNodeClass(state)]?.dataType;

/**
 * Send Tiles
 */
export const getJourneySendTiles = (state: WorkflowsRootState) => {
  const nodes = getNodes(state);
  return nodes.filter((node) => Object.values(SEND_ACTION_NODE_TYPES).includes(node.type));
};
export const getWorkflowSendTilesCount = (state: WorkflowsRootState) =>
  getJourneySendTiles(state).length;
